// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE DOCUMENT VIEWER'S RULES, WITHOUT REACT OR THE NETWORK (Jesse, 2026-10-28) — evaluated by
// backend/test/documentViewerCore.test.js, used by utils/openDocument and pages/DocumentViewer.
//
// WHERE VIEW OPENS (chooseViewer):
//   · native AND the binary carries @capacitor/browser (Capacitor.isPluginAvailable('Browser')) → 'browser': the
//     in-app browser over the app, at the real signed URL.
//   · native WITHOUT it (iOS 1.0.2 in the App Store; any Android build without it) → 'in-app': the app's own viewer
//     route. 🚨 Not a navigation to the API URL: Capacitor 8 hands every top-level navigation off the app's origin to
//     the system browser (iOS WebViewDelegationHandler → UIApplication.shared.open; Android Bridge.launchIntent →
//     ACTION_VIEW), and capacitor.config.ts has no server.allowNavigation — changing that is a native build.
//   · web, the tap still a live user gesture → 'new-tab' at the real URL (the browser's own PDF viewer, the real
//     file name, nothing to load). Otherwise → 'in-app' (a tab opened after the gesture expired would be blocked).
//   · override 'in-app' (window.__doorlyViewerOverride, the E2E hook) forces the in-app viewer — the path every
//     borrower without the plugin gets, over the same authenticated link, so forcing it can show nothing extra.
//     Any other value is ignored; it can never select the plugin or a navigation.
// ─────────────────────────────────────────────────────────────────────────────

export function chooseViewer({ native = false, browserAvailable = false, userActive = false, override = null } = {}) {
  if (override === 'in-app') return 'in-app';
  if (native) return browserAvailable ? 'browser' : 'in-app';
  return userActive ? 'new-tab' : 'in-app';
}

/** 'pdf' | 'image' | 'other', from the response's Content-Type. */
export function contentKind(contentType) {
  const t = String(contentType || '').toLowerCase().split(';')[0].trim();
  if (t === 'application/pdf') return 'pdf';
  if (/^image\/(png|jpeg|gif|webp|heic|heif)$/.test(t)) return 'image';
  return 'other';
}

/**
 * Mint the five-minute link and fetch the bytes. An expired or refused link (401) is minted again ONCE, then fetched
 * again; anything else that is not ok is an error. deps: mint() → absolute URL; fetchUrl(url) → { status, ok,
 * contentType, arrayBuffer() }.
 * → { bytes: ArrayBuffer, contentType, kind, attempts }
 */
export async function loadDocumentBytes({ mint, fetchUrl }) {
  let attempts = 1;
  let res = await fetchUrl(await mint());
  if (res.status === 401) {
    attempts = 2;
    res = await fetchUrl(await mint());
  }
  if (!res.ok) {
    const err = new Error(`document fetch failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  const bytes = await res.arrayBuffer();
  return { bytes, contentType: res.contentType, kind: contentKind(res.contentType), attempts };
}

/** The device pixel ratio a page is drawn at: crisp on phones, capped so a long file stays inside iOS canvas memory. */
export function renderRatio(devicePixelRatio, numPages) {
  const dpr = Number(devicePixelRatio) > 0 ? Number(devicePixelRatio) : 1;
  return Math.min(dpr, numPages > 12 ? 1.5 : 2);
}

/**
 * Draw every page of a PDF, in order. deps: pdfjs (the pdf.js module), data (bytes), width (CSS px of the column),
 * ratio(numPages) → pixel ratio, makeCanvas(pageNumber, { width, height, cssWidth, cssHeight }) → a canvas,
 * onPage(pageNumber, numPages), zoom (1 = fit width), options (extra getDocument options).
 * → numPages
 */
export async function renderPdfPages({ pdfjs, data, width, ratio, makeCanvas, onPage = () => {}, zoom = 1, options = {} }) {
  const task = pdfjs.getDocument({ data, isEvalSupported: false, ...options });
  const doc = await task.promise;
  try {
    const n = doc.numPages;
    const r = ratio(n);
    for (let i = 1; i <= n; i += 1) {
      const page = await doc.getPage(i);
      const base = page.getViewport({ scale: 1 });
      const fit = (width / base.width) * zoom;
      const vp = page.getViewport({ scale: fit * r });
      const canvas = makeCanvas(i, { width: Math.floor(vp.width), height: Math.floor(vp.height),
        cssWidth: Math.floor(base.width * fit), cssHeight: Math.floor(base.height * fit) });
      await page.render({ canvas, viewport: vp }).promise;
      onPage(i, n);
    }
    return n;
  } finally {
    doc.destroy();
  }
}

/** Back: one step back in the app's history when the viewer was opened from inside the app, else Documents. */
export function backTarget(historyIdx) {
  return Number(historyIdx) > 0 ? -1 : '/documents';
}
